export interface SkillData {
  id: string;
  name: string;
  hoursRequired: number; // self-study hours
  bootcampHours: number;
  courseCost: number; // self-study course cost
  bootcampCost: number;
  productivityMultiplier: number; // for AI productivity calc
  category: 'technical' | 'creative' | 'business' | 'automation';
  premiumByCategory: Record<string, number>;
}

export const skills: SkillData[] = [
  {
    id: 'prompt-engineering',
    name: 'Prompt Engineering',
    hoursRequired: 40,
    bootcampHours: 16,
    courseCost: 99,
    bootcampCost: 499,
    productivityMultiplier: 1.6,
    category: 'technical',
    premiumByCategory: {
      tech: 0.12,
      marketing: 0.10,
      creative: 0.11,
      finance: 0.08,
      operations: 0.09,
      legal: 0.09,
      consulting: 0.10,
      sales: 0.08,
      hr: 0.08,
      healthcare: 0.07
    }
  },
  {
    id: 'python-ai',
    name: 'Python for AI / Data',
    hoursRequired: 120,
    bootcampHours: 80,
    courseCost: 199,
    bootcampCost: 2499,
    productivityMultiplier: 2.1,
    category: 'technical',
    premiumByCategory: {
      tech: 0.22,
      marketing: 0.14,
      creative: 0.10,
      finance: 0.18,
      operations: 0.15,
      legal: 0.10,
      consulting: 0.14,
      sales: 0.10,
      hr: 0.11,
      healthcare: 0.12
    }
  },
  {
    id: 'ml-ops',
    name: 'Machine Learning / MLOps',
    hoursRequired: 300,
    bootcampHours: 200,
    courseCost: 499,
    bootcampCost: 7999,
    productivityMultiplier: 2.4,
    category: 'technical',
    premiumByCategory: {
      tech: 0.28,
      marketing: 0.12,
      creative: 0.08,
      finance: 0.15,
      operations: 0.12,
      legal: 0.06,
      consulting: 0.12,
      sales: 0.07,
      hr: 0.07,
      healthcare: 0.11
    }
  },
  {
    id: 'generative-ai-content',
    name: 'Generative AI Content',
    hoursRequired: 50,
    bootcampHours: 20,
    courseCost: 149,
    bootcampCost: 699,
    productivityMultiplier: 3.8,
    category: 'creative',
    premiumByCategory: {
      tech: 0.08,
      marketing: 0.18,
      creative: 0.20,
      finance: 0.06,
      operations: 0.08,
      legal: 0.07,
      consulting: 0.09,
      sales: 0.10,
      hr: 0.07,
      healthcare: 0.06
    }
  },
  {
    id: 'ai-analytics',
    name: 'AI-Powered Analytics',
    hoursRequired: 80,
    bootcampHours: 40,
    courseCost: 249,
    bootcampCost: 1299,
    productivityMultiplier: 2.1,
    category: 'business',
    premiumByCategory: {
      tech: 0.15,
      marketing: 0.16,
      creative: 0.09,
      finance: 0.20,
      operations: 0.16,
      legal: 0.10,
      consulting: 0.18,
      sales: 0.14,
      hr: 0.13,
      healthcare: 0.12
    }
  },
  {
    id: 'automation-rpa',
    name: 'Automation & RPA',
    hoursRequired: 100,
    bootcampHours: 60,
    courseCost: 199,
    bootcampCost: 1799,
    productivityMultiplier: 1.8,
    category: 'automation',
    premiumByCategory: {
      tech: 0.14,
      marketing: 0.10,
      creative: 0.06,
      finance: 0.14,
      operations: 0.19,
      legal: 0.12,
      consulting: 0.11,
      sales: 0.09,
      hr: 0.12,
      healthcare: 0.11
    }
  },
  {
    id: 'ai-strategy',
    name: 'AI Strategy & Implementation',
    hoursRequired: 90,
    bootcampHours: 48,
    courseCost: 299,
    bootcampCost: 1999,
    productivityMultiplier: 1.5,
    category: 'business',
    premiumByCategory: {
      tech: 0.12,
      marketing: 0.13,
      creative: 0.08,
      finance: 0.13,
      operations: 0.15,
      legal: 0.09,
      consulting: 0.19,
      sales: 0.11,
      hr: 0.11,
      healthcare: 0.10
    }
  },
  {
    id: 'ai-productivity',
    name: 'AI Productivity Tools',
    hoursRequired: 30,
    bootcampHours: 12,
    courseCost: 79,
    bootcampCost: 399,
    productivityMultiplier: 1.8,
    category: 'automation',
    premiumByCategory: {
      tech: 0.08,
      marketing: 0.11,
      creative: 0.12,
      finance: 0.09,
      operations: 0.12,
      legal: 0.10,
      consulting: 0.10,
      sales: 0.11,
      hr: 0.10,
      healthcare: 0.09
    }
  }
];

export const learningPaths = [
  { id: 'self-study', name: 'Self-study (online courses)', multiplier: 1 },
  { id: 'bootcamp', name: 'Bootcamp / intensive program', multiplier: 1 },
  { id: 'university', name: 'University / certification', multiplier: 1.5 }
];

export function getSkillById(id: string): SkillData | undefined {
  return skills.find(s => s.id === id);
}

export function getSkillPremium(skillId: string, category: string): number {
  const skill = getSkillById(skillId);
  if (!skill) return 0;
  return skill.premiumByCategory[category] ?? 0.10;
}
